import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtDT } from "../../lib/constants";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";
import { EnvelopeSimple, Phone, MapPin, Calculator, ChatCircleDots, Lightning, CircleNotch } from "@phosphor-icons/react";
import AdminNav from "../../components/AdminNav";

const TYPE_CLS = { "Call Back": "status-warning", Enquiries: "status-info" };
const STATUS_CLS = { NEW: "status-warning", CLOSED: "status-success", REJECTED: "status-danger" };
const SOURCE_ICON = { PUBLIC_ESTIMATE: ChatCircleDots, INTERIM_HOMEPAGE: Lightning };
const SOURCE_LABEL = { PUBLIC_ESTIMATE: "Call Back", INTERIM_HOMEPAGE: "Priority Access" };
const routeStr = (o, d) => (o && d ? `${o} → ${d}` : null);

function LeadDrawer({ emailKey, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    if (!emailKey) return;
    setLoading(true);
    api.get(`/admin/service-enquiries/leads/activity`, { params: { email: emailKey } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.displayMessage || "Could not load activity"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [emailKey]);

  const act = async (id, kind) => {
    const reason = window.prompt(`Reason to ${kind} this record (min 6 chars):`);
    if (!reason || reason.trim().length < 6) return toast.error("Reason must be at least 6 characters");
    setBusyId(id);
    try {
      await api.post(`/admin/service-enquiries/${id}/${kind}`, { reason: reason.trim() });
      toast.success(`Record ${kind === "close" ? "closed" : "rejected"}`);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.displayMessage || "Action failed");
    } finally { setBusyId(null); }
  };

  return (
    <Sheet open={!!emailKey} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="lead-drawer">
        <SheetHeader>
          <SheetTitle className="heading text-xl tracking-tight flex items-center gap-2">
            <EnvelopeSimple size={18} weight="bold" /> {emailKey || ""}
          </SheetTitle>
          <SheetDescription className="sr-only">Complete website activity for this email.</SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="mt-6 text-sm text-[hsl(var(--muted))] flex items-center gap-2" data-testid="lead-drawer-loading"><CircleNotch className="animate-spin" size={14} /> Loading activity…</div>
        ) : data ? (
          <div className="mt-4 space-y-6">
            <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="lead-drawer-header">
              <span className={`status-pill ${TYPE_CLS[data.type] || "status-info"}`}>{data.type}</span>
              {data.phone && <span className="flex items-center gap-1.5 text-[hsl(var(--muted))]"><Phone size={13} /> {data.phone}</span>}
            </div>

            {/* Enquiry / Callback records — actionable */}
            <div data-testid="lead-drawer-enquiries">
              <div className="overline mb-2">Enquiry &amp; callback records ({data.enquiries.length})</div>
              {data.enquiries.length === 0 ? (
                <div className="text-xs text-[hsl(var(--muted))]">No callback / priority-access records.</div>
              ) : (
                <div className="space-y-3">
                  {data.enquiries.map((r) => {
                    const Icon = SOURCE_ICON[r.source] || ChatCircleDots;
                    return (
                      <div key={r.id} className="grid-card p-3" data-testid={`lead-enquiry-${r.public_request_id}`}>
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <span className="status-pill status-info flex items-center gap-1"><Icon size={12} /> {SOURCE_LABEL[r.source] || r.source}</span>
                          <span className={`status-pill ${STATUS_CLS[r.status] || "status-info"}`} data-testid="lead-enquiry-status">{r.status}</span>
                          <span className="id-chip">{r.public_request_id}</span>
                          <span className="text-xs text-[hsl(var(--muted))] ml-auto">{fmtDT(r.created_at)}</span>
                        </div>
                        {routeStr(r.inferred_source_airport, r.inferred_destination_airport) && (
                          <div className="text-sm flex items-center gap-1.5"><MapPin size={13} className="text-[hsl(var(--muted))]" /> {routeStr(r.inferred_source_airport, r.inferred_destination_airport)}</div>
                        )}
                        {r.reason_summary && <div className="text-xs text-[hsl(var(--fg))] mt-1.5">{r.reason_summary}</div>}
                        {Array.isArray(r.reason_codes) && r.reason_codes.length > 0 && (
                          <div className="text-[11px] text-[hsl(var(--muted))] mt-1">Reasons: {r.reason_codes.map((rc) => ({ NO_ACTIVE_PRIORITY_ROUTE: "No active route", NO_SERVICEABLE_ORIGIN_AIRPORT: "Origin not serviceable", NO_SERVICEABLE_DESTINATION_AIRPORT: "Destination not serviceable", ZONE_D: "Zone D — manual review", AIR_FREIGHT_NOT_CONFIGURED: "Air Freight not configured", HELPER_NOT_CONFIGURED: "Load Assist not configured", ADDRESS_UNVERIFIED: "Address unverified", PACKAGE_PROFILE_UNSUPPORTED: "Package profile unsupported", FIXED_FEE_NOT_CONFIGURED: "Fixed fee not configured", LOCAL_MOVEMENT_NOT_CONFIGURED: "Local Movement not configured" }[rc] || "Not serviceable")).join(", ")}</div>
                        )}
                        {r.status === "NEW" && (
                          <div className="flex gap-2 mt-2">
                            <button className="btn-ghost text-xs" disabled={busyId === r.id} onClick={() => act(r.id, "close")} data-testid="lead-enquiry-close-btn">Mark contacted / close</button>
                            <button className="btn-ghost text-xs text-[hsl(var(--danger))]" disabled={busyId === r.id} onClick={() => act(r.id, "reject")} data-testid="lead-enquiry-reject-btn">Reject</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Check Price activity — read-only history */}
            <div data-testid="lead-drawer-attempts">
              <div className="overline mb-2 flex items-center gap-1.5"><Calculator size={13} /> Website estimate activity ({data.estimate_attempts.length})</div>
              {data.estimate_attempts.length === 0 ? (
                <div className="text-xs text-[hsl(var(--muted))]">No Check Price activity.</div>
              ) : (
                <div className="space-y-2">
                  {data.estimate_attempts.map((a) => (
                    <div key={a.public_attempt_id} className="border-l-2 border-[hsl(var(--border))] pl-3 py-1" data-testid={`lead-attempt-${a.public_attempt_id}`}>
                      <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted))]">
                        <span>{fmtDT(a.created_at)}</span>
                        <span className="id-chip">{a.public_attempt_id}</span>
                        {a.converted_to_enquiry && <span className="status-pill status-success">Converted</span>}
                      </div>
                      <div className="text-sm flex items-center gap-1.5 mt-0.5">
                        <MapPin size={13} className="text-[hsl(var(--muted))]" />
                        {routeStr(a.inferred_source_airport, a.inferred_destination_airport) || <span className="text-[hsl(var(--muted))]">Route not inferred</span>}
                      </div>
                      <div className="text-xs text-[hsl(var(--fg))] mt-0.5">{a.message_shown || a.bookability_status}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

// Admin → Leads (Super Admin only). Aggregated PUBLIC-interest view: one row per normalized email.
// No Lead-level lifecycle; individual records are actioned inside the drawer.
export default function Leads() {
  const { profile } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/service-enquiries")
      .then((r) => setRows(r.data.leads || []))
      .catch((e) => toast.error(e.displayMessage))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (!profile) return null;
  if (profile.role !== "SUPER_ADMIN") return <Navigate to="/app" replace />;

  return (
    <>
    <div className="max-w-[1600px] mx-auto px-6 pt-6"><AdminNav /></div>
    <div className="max-w-[1400px] mx-auto p-6" data-testid="leads-page">
      <div className="mb-6">
        <div className="overline">PRE-BOOKING LEADS</div>
        <h1 className="heading text-3xl">Leads</h1>
        <p className="text-sm text-[hsl(var(--muted))] mt-1">Public interest aggregated by email — Check Price, Priority Access and callback requests. One row per email.</p>
      </div>

      {loading ? (
        <div className="grid-card p-6 text-sm text-[hsl(var(--muted))]" data-testid="leads-loading">Loading leads…</div>
      ) : rows.length === 0 ? (
        <div className="grid-card p-6 text-sm text-[hsl(var(--muted))]" data-testid="leads-empty">No leads yet.</div>
      ) : (
        <div className="grid-card overflow-hidden" data-testid="leads-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[hsl(var(--border))]">
                <th className="px-4 py-3 overline font-normal">Email</th>
                <th className="px-4 py-3 overline font-normal">Type</th>
                <th className="px-4 py-3 overline font-normal">Latest Source</th>
                <th className="px-4 py-3 overline font-normal">Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.email_key} className="border-b border-[hsl(var(--border))] last:border-0 hover:bg-[hsl(var(--muted)/0.06)] cursor-pointer transition-colors"
                    onClick={() => setActive(r.email_key)} data-testid={`lead-row-${r.email_key}`}>
                  <td className="px-4 py-3 font-medium" data-testid="lead-email">{r.email}</td>
                  <td className="px-4 py-3"><span className={`status-pill ${TYPE_CLS[r.type] || "status-info"}`} data-testid="lead-type">{r.type}</span></td>
                  <td className="px-4 py-3 text-[hsl(var(--muted))]" data-testid="lead-latest-source">{r.latest_source || "—"}</td>
                  <td className="px-4 py-3 text-[hsl(var(--muted))]" data-testid="lead-last-activity">{r.last_activity ? fmtDT(r.last_activity) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LeadDrawer emailKey={active} onClose={() => setActive(null)} onChanged={load} />
    </div>
    </>
  );
}
